import { useTranslation } from "react-i18next";

import { Mascot } from "../components/Mascot";
import { Shot } from "../components/Shot";
import { MASCOTS } from "../components/mascots";
import { useHasKey } from "../components/useHasKey";
import { SHOTS } from "../components/shots";

/**
 * The order runs outward from the message: how a session starts, what protects the traffic,
 * then what protects the device the traffic lands on, and finally what the client is built
 * from. The last row names a pre-release trade-off rather than hiding it.
 */
const ROWS = [
  "handshake",
  "messages",
  "groups",
  "groupCalls",
  "attachments",
  "onDevice",
  "phrase",
  "verify",
  "appLock",
  "sync",
  "libraries",
] as const;

/**
 * A definition list, not cards: this section is for the reader who wants to check the claims
 * above against something specific, and a term-definition pair is the honest shape for that.
 * The mono term in its own column is where the page looks most like a spec sheet, which is
 * the intended impression.
 */
export function Security() {
  const { t } = useTranslation();
  const has = useHasKey();

  const rows = ROWS.filter((row) => has(`security.${row}.term`));

  return (
    <section className="section" id="security">
      <div className="section__head">
        <span className="section__eyebrow">05</span>
        <h2 className="section__heading">{t("security.heading")}</h2>
        {has("security.lede") ? <p className="section__lede">{t("security.lede")}</p> : null}
      </div>

      {/* The controls below are settings a visitor can actually go and look at, so the
          section shows them on both clients before it starts explaining the cryptography.
          The band borrows the showcase grid rather than defining its own — same three
          columns, same breakpoint — because it is the same shape: figure, desktop, phone. */}
      <div className="showcase__frames showcase__frames--aside">
        {/* The walking figure, on the LEFT because that is the side its direction wants: it
            strides toward the viewer's right, so from here it walks into the section. The one
            frame with no prop, which suits a band whose own subject is a list of settings. */}
        <Mascot className="showcase__aside" mascot={MASCOTS.walk} />
        <Shot shot={SHOTS.settingsPrivacy} altKey="shots.settingsPrivacy.alt" />
        <Shot shot={SHOTS.phonePrivacy} altKey="shots.phonePrivacy.alt" />
      </div>

      <dl className="spec">
        {rows.map((row) => (
          <div className="spec__row" key={row}>
            <dt className="spec__term">{t(`security.${row}.term`)}</dt>
            <dd className="spec__definition">{t(`security.${row}.definition`)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
